import { Injectable } from '@angular/core';
import { SelectOptionEntity } from '@gishub/domain';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { UbigeoRepositoryProvider } from '../../shared/providers/repository-providers';
import { UbigeoState } from '../states/ubigeo-state';
import { LoadingProvider } from '../../shared/providers/loading-provider';
import { MapProvider } from '../providers/map-provider';
import { FullExtentProvider } from '../providers/full-extent-provider';
import { LayerProvider } from '../providers/layer-provider';
import { PopupInteractionProvider } from '../providers/popup-interaction-provider';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

const UBIGEO_MAP_SERVER = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer';

@Injectable({ providedIn: 'root' })
export class UbigeoNotifier extends BaseNotifier<UbigeoState> {
  private get loading() { return this.ref.read(LoadingProvider); }
  private get popupInteraction() { return this.ref.read(PopupInteractionProvider); }
  private _pendingData: any | null = null;
  private _pendingExtentData: any | null = null;
  private _mapClickHandle: { remove: () => void } | null = null;
  private _lastViewForClick: any = null;

  constructor(ref: Ref) {
    super(ref, {
      departments: [],
      selectedDepartment: null,
      selectedDepartmentName: '',
      selectedProvinceName: '',
      selectedDistrictName: '',
      provinces: [],
      selectedProvince: null,
      districts: [],
      selectedDistrict: null,
      currentGeometry: null,
    });

    // Orquestación Reactiva: Escucha cambios en el estado del mapa
    this.ref.createEffect(() => {
      const mapState = this.ref.read(MapProvider).state();
      const view = mapState.view;
      if (mapState.view && mapState.map) {
        if (this._pendingData) {
          console.log('[Ubigeo] Mapa listo, procesando highlight pendiente...');
          const data = this._pendingData;
          this._pendingData = null;
          this.processGeometryData(data);
        }
        if (this._pendingExtentData) {
          console.log('[Ubigeo] Mapa listo, aplicando constraint pendiente...');
          const data = this._pendingExtentData;
          this._pendingExtentData = null;
          this._applyConstraint(data);
        }
      }
      // Registrar/actualizar handler de click en provincias/distritos
      if (view && view !== this._lastViewForClick) {
        this._removeMapClickHandler();
        this._lastViewForClick = view;
        this._mapClickHandle = view.on('click', (event: any) => this._handleMapClick(event));
      } else if (!view) {
        this._removeMapClickHandler();
      }
    });
  }

  private _removeMapClickHandler(): void {
    if (this._mapClickHandle) {
      this._mapClickHandle.remove();
      this._mapClickHandle = null;
    }
    this._lastViewForClick = null;
  }

  private async _handleMapClick(event: any): Promise<void> {
    const state = this.getStateValue();
    if (!state.selectedDepartment) return;
    const mp = event.mapPoint;
    if (!mp) return;

    if (this.popupInteraction.state().enabled) return;

    const sr = mp.spatialReference?.wkid ?? 4326;
    const baseParams: Record<string, any> = {
      geometry: JSON.stringify({ x: mp.x, y: mp.y }),
      geometryType: 'esriGeometryPoint',
      spatialRel: 'esriSpatialRelIntersects',
      inSR: sr,
      outFields: '*',
      returnGeometry: false,
      f: 'json',
    };

    // Only process map click if at least one boundary layer is visible
    const provVisible = this._isLayerVisible('Límite provincial');
    const distVisible = this._isLayerVisible('Límite distrital');
    if (!provVisible && !distVisible) return;

    // 1) Intentar distrito primero (siempre consulta directa al MapServer)
    try {
      const distParams = { ...baseParams, where: `CODDEP = '${state.selectedDepartment}'` };
      const distRes = await fetch(`${UBIGEO_MAP_SERVER}/33/query?${new URLSearchParams(distParams)}`, { signal: AbortSignal.timeout(10_000) });
      const feats = (await distRes.json())?.features;
      if (feats && feats.length > 0) {
        const a = feats[0].attributes;
        const code = String(a.UBIGEO ?? '');
        const name = String(a.NOMBDIST ?? '').trim();
        if (code) {
          if (code === state.selectedDistrict) {
            await this.selectProvince('', '');
            return;
          }
          const provCode = code.substring(0, 4);
          if (provCode && provCode !== state.selectedProvince) {
            const provName = String(a.NOMBPROV ?? '').trim();
            await this.selectProvince(provCode, provName);
          }
          await this.selectDistrict(code, name);
          return;
        }
      }
    } catch (ex) { console.warn('[Ubigeo] Error querying district', ex); }

    // 2) Intentar provincia
    try {
      const provParams = { ...baseParams, where: `CODDEP = '${state.selectedDepartment}'` };
      const provRes = await fetch(`${UBIGEO_MAP_SERVER}/34/query?${new URLSearchParams(provParams)}`, { signal: AbortSignal.timeout(10_000) });
      const feats = (await provRes.json())?.features;
      if (feats && feats.length > 0) {
        const a = feats[0].attributes;
        const code = String(a.CODPROV ?? '');
        const name = String(a.NOMBPROV ?? '').trim();
        if (code) {
          if (code === state.selectedProvince && !state.selectedDistrict) {
            await this.selectProvince('', '');
          } else {
            await this.selectProvince(code, name);
          }
          return;
        }
      }
    } catch (ex) { console.warn('[Ubigeo] Error querying province', ex); }

    // 3) Nada encontrado → deseleccionar provincia/distrito
    if (state.selectedProvince || state.selectedDistrict) {
      await this.selectProvince('', '');
    }
  }

  private get ubigeoRepo() {
    return this.ref.read(UbigeoRepositoryProvider).instance;
  }

  private get mapProvider() {
    return this.ref.read(MapProvider);
  }

  private get layerProvider() {
    return this.ref.read(LayerProvider);
  }

  private _isLayerVisible(layerName: string): boolean {
    const layers = this.layerProvider.state().layers;
    for (const cat of layers) {
      if (!cat.layers) continue;
      for (const sub of cat.layers) {
        if (sub.name === layerName) return !!(cat.visible && sub.visible);
      }
    }
    return false;
  }

  override async build(): Promise<void> {
    this.loading.notifier.start();
    try {
      const res = await this.ubigeoRepo.getDepartments();
      const departments: SelectOptionEntity[] = (res.data ?? []).map(d => ({
        label: d.name,
        value: d.code,
      }));

      const defaultCode = this.ubigeoRepo.getDefaultDepartmentCode();
      const defaultDept = departments.find(d => d.value === defaultCode) ?? departments[0];

      this.setState({
        ...this.getStateValue(),
        departments,
        selectedDepartment: defaultDept?.value ?? null,
        selectedDepartmentName: defaultDept?.label ?? '',
      });
      this.loading.notifier.stop();

      if (defaultDept) {
        await this.loadProvinces(defaultDept.value);
        const geomRes = await this.ubigeoRepo.getDepartmentGeometry(defaultDept.value);
        if (geomRes.success) {
          this._setGeometryFromResponse(geomRes.data);
          this._setDepartmentConstraint(geomRes.data);
          this.highlightOnMap(geomRes.data);
        }
      }
    } catch (ex: any) {
      console.error('Error loading departments for Ubigeo', ex);
      this.loading.notifier.stop();
    }
  }

  async selectDepartment(code: string): Promise<void> {
    const dept = this.getStateValue().departments.find(d => d.value === code);
    this.setState({
      ...this.getStateValue(),
      selectedDepartment: code,
      selectedDepartmentName: dept?.label ?? '',
      selectedProvince: null,
      provinces: [],
      selectedDistrict: null,
      districts: [],
    });
    await this.loadProvinces(code);
    const geomRes = await this.ubigeoRepo.getDepartmentGeometry(code);
    if (geomRes.success) {
      this._setGeometryFromResponse(geomRes.data);
      this._setDepartmentConstraint(geomRes.data);
      this.highlightOnMap(geomRes.data);
    }
  }

  async selectProvince(code: string, name?: string): Promise<void> {
    const prov = this.getStateValue().provinces.find(p => p.value === code);
    this.setState({
      ...this.getStateValue(),
      selectedProvince: code,
      selectedProvinceName: prov?.label ?? name ?? '',
      selectedDistrict: null,
      selectedDistrictName: '',
      districts: [],
    });
    if (code) {
      await this.loadDistricts(code);
      const geomRes = await this.ubigeoRepo.getProvinceGeometry(code);
      if (geomRes.success) {
        this._setGeometryFromResponse(geomRes.data);
        this.highlightOnMap(geomRes.data);
      }
    } else {
      const deptCode = this.getStateValue().selectedDepartment;
      if (deptCode) {
        const geomRes = await this.ubigeoRepo.getDepartmentGeometry(deptCode);
        if (geomRes.success) {
          this._setGeometryFromResponse(geomRes.data);
          this.highlightOnMap(geomRes.data);
        }
      }
    }
  }

  async selectDistrict(code: string, name?: string): Promise<void> {
    const dist = this.getStateValue().districts.find(d => d.value === code);
    this.setState({
      ...this.getStateValue(),
      selectedDistrict: code || null,
      selectedDistrictName: dist?.label ?? name ?? '',
    });
    if (code) {
      const geomRes = await this.ubigeoRepo.getDistrictGeometry(code);
      if (geomRes.success) {
        this._setGeometryFromResponse(geomRes.data);
        this.highlightOnMap(geomRes.data);
      }
    } else {
      const provCode = this.getStateValue().selectedProvince;
      if (provCode) {
        const geomRes = await this.ubigeoRepo.getProvinceGeometry(provCode);
        if (geomRes.success) {
          this._setGeometryFromResponse(geomRes.data);
          this.highlightOnMap(geomRes.data);
        }
      }
    }
  }

  private highlightOnMap(esriJson: any): void {

    const mapState = this.mapProvider.state();
    if (!mapState.view || !mapState.map) {
      console.log('[Ubigeo] Mapa no disponible aún, guardando en pendiente.');
      this._pendingData = esriJson;
      return;
    }
    this.processGeometryData(esriJson);
  }

  private _setDepartmentConstraint(esriJson: any): void {
    const view = this.ref.read(MapProvider).state().view;
    if (!view) {
      this._pendingExtentData = esriJson;
      return;
    }
    this._applyConstraint(esriJson);
  }

  private _applyConstraint(esriJson: any): void {
    const view = this.ref.read(MapProvider).state().view;
    if (!view) return;
    const raw = esriJson?.features?.[0]?.geometry ?? null;
    if (!raw) return;
    try {
      const spatialRef = esriJson.spatialReference || raw.spatialReference;
      let polygon = Polygon.fromJSON({ ...raw, spatialReference: spatialRef });
      if (polygon.spatialReference?.isGeographic) {
        polygon = webMercatorUtils.geographicToWebMercator(polygon) as Polygon;
      }
      const extent = polygon.extent;
      if (extent) {
        view.constraints = {
          geometry: extent.expand(1.3),
          minZoom: 7,
          maxZoom: 19,
        };
      }
    } catch (ex) {
      console.warn('[Ubigeo] Error setting extent constraint', ex);
    }
  }

  private _setGeometryFromResponse(esriJson: any): void {
    let geometry = esriJson?.features?.[0]?.geometry ?? null;
    if (geometry && !geometry.spatialReference && esriJson?.spatialReference) {
      geometry.spatialReference = esriJson.spatialReference;
    }
    geometry = geometry ? Polygon.fromJSON(geometry) : null;
    if (geometry?.spatialReference?.isGeographic) {
      geometry = webMercatorUtils.geographicToWebMercator(geometry);
    }
    this.setState({ ...this.getStateValue(), currentGeometry: geometry });
    this.ref.read(FullExtentProvider).notifier.setGeometry(geometry);
  }

  private async processGeometryData(esriJson: any): Promise<void> {
    const mapState = this.mapProvider.state();
    const map = mapState.map;
    const view = mapState.view;

    if (!map || !view || !esriJson.features || esriJson.features.length === 0) {
      console.warn('[Ubigeo] Datos insuficientes para renderizar geometría.');
      return;
    }

    console.log('[Ubigeo] Renderizando highlight sobre el mapa...');

    // 1. Obtener o crear capa dedicada
    let layer = map.findLayerById('selected-ubigeo-layer') as GraphicsLayer;
    if (!layer) {
      layer = new GraphicsLayer({
        id: 'selected-ubigeo-layer',
        listMode: 'hide',
        effect: 'bloom(1.5, 0.5px, 0.1) drop-shadow(2px, 2px, 4px, rgba(0,0,0,0.6))'
      });
      map.add(layer);
    }
    layer.removeAll();

    // 2. Simbología Profesional: STROKE ONLY (Sin Fill)
    const symbol = new SimpleFillSymbol({
      color: null as any, // Sin relleno
      outline: new SimpleLineSymbol({
        color: new Color([0, 200, 255, 1]), // Púrpura (moderno y único)
        width: 1.5,
        style: 'solid'
      })
    });

    // 3. Inyección de Spatial Reference y creación de Graphics
    const globalSR = esriJson.spatialReference;
    const graphics = esriJson.features.map((feature: any) => {
      const geomData = { ...feature.geometry };
      if (!geomData.spatialReference && globalSR) {
        geomData.spatialReference = globalSR;
      }

      return new Graphic({
        geometry: Polygon.fromJSON(geomData),
        symbol: symbol
      });
    });

    layer.addMany(graphics);

    // 4. Zoom fluido al Envelope de la geometría
    if (graphics.length > 0) {
      let extent = graphics[0].geometry?.extent ?? null;
      graphics.forEach((g: Graphic) => {
        if (g.geometry?.extent) {
          extent = extent ? extent.union(g.geometry.extent) : g.geometry.extent;
        }
      });

      if (extent) {
        await view.goTo(extent.expand(1.2), {
          animate: true,
          duration: 1000,
          easing: 'ease-in-out'
        });
      }
    }
  }

  private async loadProvinces(departmentCode: string): Promise<void> {
    try {
      const res = await this.ubigeoRepo.getProvincesByDepartment(departmentCode);
      const provinces: SelectOptionEntity[] = [
        { label: 'Toda la Región', value: '' },
        ...(res.data ?? []).map(p => ({ label: p.name, value: p.code })),
      ];
      this.setState({ ...this.getStateValue(), provinces });
    } catch (ex: any) {
      console.error('Error loading provinces', ex);
    }
  }

  private async loadDistricts(provinceCode: string): Promise<void> {
    try {
      const res = await this.ubigeoRepo.getDistrictsByProvince(provinceCode);
      const districts: SelectOptionEntity[] = [
        { label: 'Todos los Distritos', value: '' },
        ...(res.data ?? []).map(d => ({ label: d.name, value: d.code })),
      ];
      this.setState({ ...this.getStateValue(), districts });
    } catch (ex: any) {
      console.error('Error loading districts', ex);
    }
  }
}
